import React, { useId, useState } from "react";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";
import Modal from "../../../components/shared/Modal";
import { toast } from "../../../components/shared/toast";

const REASONS = [
  "Not enough experience",
  "Skills mismatch",
  "Culture fit concerns",
  "Position filled",
  "Salary expectations too high",
  "Other",
];

export default function RejectionModal({ candidateId, jobId, candidateName, candidateEmail, onClose, onDone }) {
  const [reason, setReason] = useState(REASONS[0]);
  const [feedback, setFeedback] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const reasonId = useId();
  const feedbackId = useId();

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const feedbackSummary = feedback.trim()
        ? `Specific feedback: ${feedback.trim()}`
        : "";

      // 1) Update candidate status (best-effort — endpoint may or may not exist)
      try {
        await axiosInstance.post(
          `${baseUrl}/job-posting/candidate/${candidateId}/reject`,
          { reason, feedback }
        );
      } catch (_) {
        // Status-update endpoint may not exist yet; the email send below still happens
      }

      // 2) Trigger stage-transition email if toggled on
      if (sendEmail) {
        await axiosInstance.post(`${baseUrl}/job-posting/stage-emails/send`, {
          stageTo: "REJECTED",
          candidateId: Number(candidateId),
          jobId: jobId ? Number(jobId) : null,
          recipientEmail: candidateEmail || null,
          feedbackSummary,
          variables: {
            candidateName: candidateName || `Candidate #${candidateId}`,
            jobTitle: jobId ? `Job #${jobId}` : "the role",
            companyName: "Ample Hire",
            recruiterName: "the hiring team",
            feedbackSummary,
          },
        });
      }

      toast.success("Candidate rejected", {
        description: sendEmail ? "Rejection email sent." : undefined,
      });
      onDone?.({ rejected: true, emailed: sendEmail });
    } catch (e) {
      setError(e?.message || "Failed to reject candidate");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Reject candidate"
      description={candidateName || `Candidate #${candidateId}`}
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded border border-slate-300 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-autofocus
            className="px-3 py-1.5 rounded bg-red-600 text-white text-sm disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
          >
            {submitting ? "Rejecting…" : "Confirm reject"}
          </button>
        </div>
      }
    >
      <label htmlFor={reasonId} className="block text-sm font-medium text-vf-base mb-1">
        Reason
      </label>
      <select
        id={reasonId}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-full mb-4 px-3 py-2 border border-slate-300 rounded text-sm"
      >
        {REASONS.map((r) => <option key={r}>{r}</option>)}
      </select>

      <label htmlFor={feedbackId} className="block text-sm font-medium text-vf-base mb-1">
        Specific feedback for the candidate <span className="text-xs text-vf-muted">(optional)</span>
      </label>
      <textarea
        id={feedbackId}
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder="What could they improve? Will be appended to the rejection email if enabled."
        className="w-full mb-3 px-3 py-2 border border-slate-300 rounded text-sm resize-none"
        rows={4}
      />

      <label className="flex items-center gap-2 mb-1 cursor-pointer">
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
        />
        <span className="text-sm text-vf-base">Send rejection email to candidate</span>
      </label>

      {error && (
        <div role="alert" className="mt-3 p-2 rounded bg-red-50 text-red-700 text-xs border border-red-200">
          {error}
        </div>
      )}
    </Modal>
  );
}
